import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Code, Heading, Text } from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  context?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { 
      hasError: true, 
      error,
      errorInfo: null
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });
    
    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReportError = () => {
    const { error, errorInfo } = this.state;
    const subject = encodeURIComponent('Error Report');
    const body = encodeURIComponent(
      `Please describe what you were doing when the error occurred:\n\n` +
      `Error: ${error?.toString()}\n\n` +
      `Component Stack: ${errorInfo?.componentStack || 'No stack trace available'}`
    );
    window.location.href = `mailto:your-support@example.com?subject=${subject}&body=${body}`;
  };

  public render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, context = 'application' } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback && React.isValidElement(fallback)) {
        return React.cloneElement(fallback as React.ReactElement, { 
          error,
          resetErrorBoundary: this.handleReload 
        });
      }

      // Default error UI
      return (
        <Box p={6} maxW="container.md" mx="auto" mt={10} borderWidth="1px" borderRadius="lg" overflow="hidden">
          <Heading as="h1" size="lg" color="red.500" mb={4}>
            Oops! Something went wrong
          </Heading>
          
          <Text mb={4}>
            We're sorry, but {context} encountered an unexpected error. Our team has been notified.
          </Text>

          {process.env.NODE_ENV === 'development' && (
            <Box mb={4} p={3} bg="gray.100" borderRadius="md" fontSize="sm">
              <Text fontWeight="bold" mb={2}>Error Details (Development Only):</Text>
              <Code display="block" whiteSpace="pre" p={2} overflowX="auto">
                {error?.toString()}
                {errorInfo?.componentStack}
              </Code>
            </Box>
          )}

          <Box mt={6} display="flex" gap={3}>
            <Button 
              colorScheme="blue" 
              onClick={this.handleReload}
              leftIcon={<ExternalLinkIcon />}
            >
              Reload {context}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={this.handleReportError}
            >
              Report Issue
            </Button>
          </Box>
        </Box>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
